import {lazy, Suspense, useState, useCallback, useMemo} from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes, useParams} from 'react-router-dom';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Grow from '@mui/material/Grow';
import Box from '@mui/material/Box';

import Layout from './components/Layout';
import {AuthProvider, RequireSignIn} from './auth/AuthContext';
import ProjectCompanyGate from './auth/ProjectCompanyGate';
import RequireAdmin from './auth/RequireAdmin';
import {TourContext} from './contexts/TourContext';
import {ColorModeContext} from './contexts/ColorModeContext';

// Route-level code splitting — each page loads only when navigated to.
// PendingDocDetailPage is especially important: it pulls in Monaco Editor (~4MB).
const AddressSearchPage = lazy(() => import('./pages/AddressSearchPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const ParametersPage = lazy(() => import('./pages/ParametersPage'));
const PendingDocsPage = lazy(() => import('./pages/PendingDocsPage'));
const PendingDocDetailPage = lazy(() => import('./pages/PendingDocDetailPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const CompanyPage = lazy(() => import('./pages/CompanyPage'));
const UdeAdminWarningsPage = lazy(() => import('./pages/ude/UdeAdminWarningsPage'));
// UDE intake (Phase 5 part 1)
const UdeProjectsPage = lazy(() => import('./pages/ude/UdeProjectsPage'));
const UdeCreateProjectPage = lazy(() => import('./pages/ude/UdeCreateProjectPage'));
const UdeDrawingsPage = lazy(() => import('./pages/ude/UdeDrawingsPage'));
const UdeReviewPage = lazy(() => import('./pages/ude/UdeReviewPage'));
const UdeCompliancePage = lazy(() => import('./pages/ude/UdeCompliancePage'));
const UdeRegelverkPage = lazy(() => import('./pages/ude/UdeRegelverkPage'));
// Sign-in (SB-23): outside the Layout, so it shows no sidebar or app bar.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const InvitationPage = lazy(() => import('./pages/InvitationPage'));

/** Redirect legacy `/upload` and `/classify` deep-links to the merged `/drawings`. */
function UdeRedirectToDrawings() {
    const {id} = useParams<{id: string}>();
    return <Navigate to={`/ude/projects/${id}/drawings`} replace/>;
}

/** Redirect legacy `/extract/:runId` to `/review?run=<runId>` — the
 *  extraction-progress UI now lives inline on the Review page. */
function UdeRedirectExtractToReview() {
    const {id, runId} = useParams<{id: string; runId: string}>();
    return <Navigate to={`/ude/projects/${id}/review?run=${runId}`} replace/>;
}

// --- Design System: The Nordic Architect ---
// See DESIGN.md for full specification.

const headingFont = '"Manrope", -apple-system, BlinkMacSystemFont, sans-serif';
const bodyFont = '"Inter", -apple-system, BlinkMacSystemFont, sans-serif';

/** Surface tier tokens per mode (see DESIGN.md §3) */
const surfaces = {
    light: {
        bg: '#fbf9f8',
        lowest: '#ffffff',
        low: '#f1efee',
        container: '#eae8e6',
        high: '#e3e1df',
        highest: '#dcdad8',
    },
    dark: {
        bg: '#0c1324',
        lowest: '#070d1f',
        low: '#151b2d',
        container: '#1c2236',
        high: '#23293c',
        highest: '#2e3447',
        bright: '#33394c',
    },
};

function getDesignTokens(mode: 'light' | 'dark') {
    const s = surfaces[mode];
    const isLight = mode === 'light';

    return createTheme({
        palette: {
            mode,
            primary: {
                main: isLight ? '#005dac' : '#bcc7de',
            },
            secondary: {
                main: isLight ? '#4e6078' : '#4edea3',
            },
            success: {
                main: isLight ? '#006b36' : '#4edea3',
            },
            // Light error/warning darkened from the MUI defaults (#F44336 /
            // #ED6C02) for WCAG AA: white-on-error 4.98:1, white-on-warning
            // 5.02:1, and both ≥4.5:1 as text on the bone-white surfaces.
            // warning.dark is pinned for tinted-chip text (5.85:1 on a 14%
            // warning tint). DESIGN.md §10 records the deviation.
            error: {
                main: isLight ? '#D32F2F' : '#ffb4ab',
            },
            warning: {
                main: isLight ? '#B45309' : '#ffb74d',
                ...(isLight ? {dark: '#8a4100'} : {}),
            },
            background: {
                default: s.bg,
                paper: isLight ? s.lowest : s.container,
            },
            text: {
                primary: isLight ? '#1b1c1c' : '#dce1fb',
                secondary: isLight ? '#4e6078' : '#c5c6cd',
            },
            divider: isLight ? 'rgba(27, 28, 28, 0.08)' : 'rgba(69, 71, 76, 0.15)',
            action: {
                hover: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                selected: isLight ? 'rgba(0, 93, 172, 0.08)' : 'rgba(188, 199, 222, 0.08)',
            },
        },
        shape: {
            borderRadius: 4,
        },
        typography: {
            fontFamily: bodyFont,
            h4: {
                fontFamily: headingFont,
                fontWeight: 700,
                letterSpacing: '-0.02em',
            },
            h5: {
                fontFamily: headingFont,
                fontWeight: 600,
                letterSpacing: '-0.02em',
            },
            h6: {
                fontFamily: headingFont,
                fontWeight: 600,
                letterSpacing: '-0.01em',
            },
            subtitle1: {
                fontFamily: headingFont,
                fontWeight: 600,
            },
            subtitle2: {
                fontFamily: headingFont,
                fontWeight: 600,
                letterSpacing: '0.05em',
            },
            body1: {
                lineHeight: 1.6,
            },
            body2: {
                lineHeight: 1.5,
            },
            caption: {
                letterSpacing: '0.02em',
            },
            button: {
                fontFamily: bodyFont,
                fontWeight: 500,
            },
        },
        components: {
            // WCAG 2.4.7: a visible focus indicator on every interactive
            // element. MUI's default focus treatment on chips/icon buttons is
            // a faint bg wash — invisible against tinted rows. The outline
            // matches the stepper nav's existing focus ring.
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${isLight ? '#005dac' : '#bcc7de'}`,
                            outlineOffset: 1,
                        },
                    },
                },
            },
            // Touch ergonomics: dense desktop targets (20px chips, small icon
            // buttons) grow on coarse pointers only — keyed on input method,
            // not viewport, so touch laptops benefit and mouse users keep the
            // dense layout.
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        '@media (pointer: coarse)': {
                            padding: 10,
                        },
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        // CSS custom properties for surface tiers and colors (accessible in inline styles)
                        '--surface-bg': s.bg,
                        '--surface-lowest': isLight ? s.lowest : (s as any).lowest,
                        '--surface-low': s.low,
                        '--surface-container': s.container,
                        '--surface-high': s.high,
                        '--surface-highest': s.highest,
                        '--color-text-secondary': isLight ? '#4e6078' : '#c5c6cd',
                        '--color-divider': isLight ? 'rgba(27, 28, 28, 0.08)' : 'rgba(69, 71, 76, 0.15)',
                        '--color-shadow': isLight ? 'rgba(27, 28, 28, 0.06)' : 'rgba(0, 0, 0, 0.3)',
                    } as any,
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none' as const,
                        fontWeight: 500,
                        borderRadius: 4,
                    },
                    contained: {
                        backgroundColor: isLight ? '#005dac' : '#bcc7de',
                        color: isLight ? '#ffffff' : '#0c1324',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: isLight ? '#004a8f' : '#ced6e8',
                            boxShadow: 'none',
                        },
                    },
                    outlined: {
                        borderColor: isLight ? 'rgba(27, 28, 28, 0.2)' : 'rgba(69, 71, 76, 0.3)',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none', // Remove MUI's default gradient overlay in dark mode
                        border: 'none',
                        boxShadow: isLight
                            ? '0 1px 3px rgba(27, 28, 28, 0.04)'
                            : 'none',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        border: 'none',
                        boxShadow: isLight
                            ? '0 1px 3px rgba(27, 28, 28, 0.04)'
                            : 'none',
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'filled' as const,
                },
                styleOverrides: {
                    root: {
                        '& .MuiFilledInput-root': {
                            backgroundColor: s.high,
                            borderRadius: '4px 4px 0 0',
                            '&:hover': {
                                backgroundColor: isLight ? s.container : s.highest,
                            },
                            '&.Mui-focused': {
                                backgroundColor: s.high,
                            },
                            '&::before': {
                                borderBottomColor: isLight ? 'rgba(27,28,28,0.2)' : 'rgba(69,71,76,0.3)',
                            },
                            '&::after': {
                                borderBottomColor: isLight ? '#005dac' : '#4edea3',
                            },
                        },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 500,
                        // Chip is not a ButtonBase — clickable chips need their
                        // own focus-visible outline (WCAG 2.4.7).
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${isLight ? '#005dac' : '#bcc7de'}`,
                            outlineOffset: 1,
                        },
                        // min-height beats the per-use `height: 20/22` sx, so
                        // tappable chips reach a usable target on touch input.
                        '@media (pointer: coarse)': {
                            '&.MuiChip-clickable': {
                                minHeight: 32,
                            },
                        },
                    },
                    colorSuccess: {borderRadius: 9999},
                    colorError: {borderRadius: 9999},
                    colorWarning: {borderRadius: 9999},
                    colorInfo: {borderRadius: 9999},
                    colorPrimary: {borderRadius: 9999},
                    colorSecondary: {borderRadius: 9999},
                },
            },
            MuiDialog: {
                defaultProps: {
                    slots: {
                        transition: Grow
                    }
                },
                styleOverrides: {
                    paper: {
                        backgroundColor: isLight
                            ? `rgba(251, 249, 248, 0.85)`
                            : `rgba(28, 34, 54, 0.7)`,
                        backdropFilter: 'blur(16px)',
                        boxShadow: isLight
                            ? '0 20px 40px rgba(27, 28, 28, 0.06)'
                            : '0 20px 40px rgba(0, 0, 0, 0.4)',
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontVariantNumeric: 'tabular-nums',
                        borderBottomColor: isLight ? 'rgba(27,28,28,0.06)' : 'rgba(69,71,76,0.12)',
                    },
                    head: {
                        backgroundColor: s.container,
                        fontWeight: 600,
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        '&::before': {display: 'none'},
                        backgroundColor: 'transparent',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: isLight
                            ? '0 1px 3px rgba(27, 28, 28, 0.06)'
                            : 'none',
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: s.low,
                        borderRight: 'none',
                    },
                },
            },
        },
    });
}

/** Everything but the sign-in page: the Layout around the app's routes, shown to a signed-in
 *  person, or to everyone while sign-in is not enforced (SB-23). */
function AppShell({onTourRequest}: {onTourRequest?: () => void}) {
    return (
        <RequireSignIn>
            <Layout onTourRequest={onTourRequest}>
                <Suspense fallback={
                    <Box sx={{display: 'flex', justifyContent: 'center', pt: 8}}>
                        <CircularProgress />
                    </Box>
                }>
                    <Routes>
                        <Route path="/" element={<Navigate to="/address-search" replace/>}/>
                        <Route path="/docs" element={<DocsPage/>}/>
                        <Route path="/docs/:sectionId" element={<DocsPage/>}/>
                        {/* The corpus tooling is the platform role's (SB-322); the API's 403 is the guard. */}
                        <Route path="/pending-docs" element={<RequireAdmin><PendingDocsPage/></RequireAdmin>}/>
                        <Route path="/pending-docs/:id" element={<RequireAdmin><PendingDocDetailPage/></RequireAdmin>}/>
                        <Route path="/parameters" element={<RequireAdmin><ParametersPage/></RequireAdmin>}/>
                        <Route path="/parameters/:paramCode" element={<RequireAdmin><ParametersPage/></RequireAdmin>}/>
                        <Route path="/address-search" element={<AddressSearchPage/>}/>
                        <Route path="/admin" element={<RequireAdmin><AdminPage/></RequireAdmin>}/>
                        <Route path="/admin/user-feedback" element={<Navigate to="/admin?tab=feedback" replace/>}/>
                        <Route path="/ude/admin/warnings" element={<RequireAdmin><UdeAdminWarningsPage/></RequireAdmin>}/>
                        <Route path="/company" element={<CompanyPage/>}/>
                        {/* UDE intake (Phase 5 part 1) + compliance report (Phase 5 part 2) */}
                        <Route path="/ude/projects" element={<UdeProjectsPage/>}/>
                        <Route path="/ude/projects/new" element={<UdeCreateProjectPage/>}/>
                        {/* A link into a project in another of the person's companies switches first (SB-321). */}
                        <Route path="/ude/projects/:id/drawings" element={<ProjectCompanyGate><UdeDrawingsPage/></ProjectCompanyGate>}/>
                        {/* Legacy routes — redirect to /drawings (the merged Upload+Classify page).
                            Kept so external links / browser bookmarks don't 404 after the merge. */}
                        <Route path="/ude/projects/:id/upload" element={<UdeRedirectToDrawings/>}/>
                        <Route path="/ude/projects/:id/classify" element={<UdeRedirectToDrawings/>}/>
                        {/* Extraction progress is now inlined on the Review page;
                            legacy /extract URL just redirects with the run_id. */}
                        <Route path="/ude/projects/:id/extract/:runId" element={<UdeRedirectExtractToReview/>}/>
                        <Route path="/ude/projects/:id/review" element={<ProjectCompanyGate><UdeReviewPage/></ProjectCompanyGate>}/>
                        <Route path="/ude/projects/:id/compliance" element={<ProjectCompanyGate><UdeCompliancePage/></ProjectCompanyGate>}/>
                        <Route path="/ude/projects/:id/regelverk" element={<ProjectCompanyGate><UdeRegelverkPage/></ProjectCompanyGate>}/>
                    </Routes>
                </Suspense>
            </Layout>
        </RequireSignIn>
    );
}

function App() {
    const [mode, setMode] = useState<'light' | 'dark'>(() => {
        const stored = localStorage.getItem('colorMode');
        if (stored === 'light' || stored === 'dark') return stored;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    const colorMode = useMemo(() => ({
        toggleColorMode: () => {
            setMode(prev => {
                const next = prev === 'light' ? 'dark' : 'light';
                localStorage.setItem('colorMode', next);
                return next;
            });
        },
        mode,
    }), [mode]);

    const theme = useMemo(() => getDesignTokens(mode), [mode]);

    const [tourHandler, setTourHandlerState] = useState<(() => void) | null>(null);

    const setTourHandler = useCallback((handler: (() => void) | null) => {
        setTourHandlerState(() => handler);
    }, []);

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <TourContext.Provider value={{setTourHandler}}>
                    <Router>
                        <AuthProvider>
                            <Routes>
                                <Route path="/login" element={<Suspense fallback={null}><LoginPage/></Suspense>}/>
                                {/* An invitation link (SB-320): open to a person with no session or company yet. */}
                                <Route path="/invitasjon/:token"
                                       element={<Suspense fallback={null}><InvitationPage/></Suspense>}/>
                                <Route path="/*" element={<AppShell onTourRequest={tourHandler ?? undefined}/>}/>
                            </Routes>
                        </AuthProvider>
                    </Router>
                </TourContext.Provider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

export default App;
